import ChangelogAccordion from 'components/changelog-page/ChangelogAccordion'
import ChangelogErrorStateComponent from 'components/changelog-page/ChangelogErrorState'
import StaticPageLayout from 'components/StaticPageLayout'
import Svg from 'components/UI/SVG'
import TitleAndSubtitle from 'components/UI/TitleAndSubtitle'
import { graphql, useStaticQuery } from 'gatsby'
import ChangelogLoadingBig from 'images/changelog-big-loading.svg'
import ChangelogLoadingSmall from 'images/changelog-small-loading.svg'
import Separator from 'images/separator.svg'
import { platformName } from 'pages/changelog'
import { AccordionWrapper } from 'pages/terms/oss'
import { ReactElement } from 'react'
import { wsApi } from 'redux/api/wsApi'
import { Flex } from 'theme-ui'
import getEnglishStrings from 'utils/getEnglishStrings'

interface Metadata {
  title?: string | null | undefined
  meta_description: string | null | undefined
  meta_keywords: string[] | null | undefined
}

export type BuildType = 'release' | 'beta' | 'guinea_pig'

function GetBuildType(beta: number): BuildType {
  if (beta === 0) {
    return 'release'
  } else if (beta === 1) {
    return 'beta'
  }
  return 'guinea_pig'
}

export default function ChangelogBase({
  metaData,
  platform,
  title,
}: {
  metaData: Metadata
  title?: string | null | undefined
  platform: string
}): ReactElement {
  const changelogData = useStaticQuery<Queries.ChangelogQuery>(graphql`
    query Changelog {
      directus {
        general_translations {
          strings {
            languages_code {
              code
            }
            changelogs
          }
        }
      }
    }
  `)

  const changelogsStrings = getEnglishStrings(changelogData.directus?.general_translations?.strings)

  const {
    data,
    isFetching,
    isError: isErrorChangelogs,
  } = wsApi.useChangelogsQuery({ platform }, { refetchOnMountOrArgChange: true })

  const isError = isErrorChangelogs || !data?.data?.[0].platform

  const filteredData = data?.data.filter(changelog => changelog.is_supported === 1)

  return (
    <StaticPageLayout
      title={metaData.title ?? platformName[filteredData?.[0].platform ?? 'Windows'] ?? ''}
      metaDescription={metaData.meta_description ?? ''}
      metaKeywords={metaData.meta_keywords ?? []}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          gap: ['4.8rem', '8rem'],
          width: '100%',
          maxWidth: 'maxContentWidth',
          mb: ['1.6rem', '8rem'],
        }}
      >
        <Flex sx={{ alignItems: 'center' }}>
          <Flex sx={{ maxWidth: '82rem', gap: '4.8rem', flexDirection: ['column', 'row'] }}>
            <Flex sx={{ flexDirection: 'column', gap: '3.2rem' }}>
              <TitleAndSubtitle
                title={
                  title ??
                  `${platformName[filteredData?.[0].platform ?? 'Windows']} ${
                    changelogsStrings?.changelogs
                  }`
                }
              />
            </Flex>
          </Flex>
        </Flex>
        <Flex sx={{ flexDirection: 'column', gap: '4.8rem', width: '100%', alignItems: 'center' }}>
          <Flex
            sx={{
              flexDirection: 'column',
              gap: ['0.8rem', '1.6rem'],
              maxWidth: 'maxContentWidth',
              width: '100%',
            }}
          >
            {isFetching && (
              <Flex sx={{ gap: '1.6rem', flexDirection: 'column', svg: { width: '100%' } }}>
                <ChangelogLoadingState />
                <ChangelogLoadingState />
                <ChangelogLoadingState />
                <ChangelogLoadingState />
                <ChangelogLoadingState />
                <ChangelogLoadingState />
                <ChangelogLoadingState />
                <ChangelogLoadingState />
                <ChangelogLoadingState />
                <ChangelogLoadingState />
              </Flex>
            )}
            {isError && (
              <Flex sx={{ gap: '1.6rem', flexDirection: 'column', svg: { width: '100%' } }}>
                <ChangelogErrorState />
              </Flex>
            )}
            {!isFetching &&
              !isError &&
              filteredData?.map(changelog => (
                <ChangelogAccordion
                  version={`${changelog.version}.${changelog.build}`}
                  buildType={GetBuildType(changelog.beta)}
                  releaseDate={changelog.release_date}
                  content={changelog.changelog}
                  href={changelog.url}
                  key={changelog.id}
                  dataTestId="changelog-accordion"
                />
              ))}
          </Flex>
        </Flex>
      </Flex>
    </StaticPageLayout>
  )
}

function ChangelogLoadingState() {
  return (
    <Flex sx={{ maxHeight: '5.4rem' }} data-testid="changelog-loading">
      <AccordionWrapper>
        <Flex sx={{ gap: '1.6rem', alignItems: 'center', width: 'fit-content', maxWidth: '22rem' }}>
          <Svg svg={ChangelogLoadingSmall} />
          <Svg svg={Separator} sx={{ maxWidth: 'fit-content' }} />
          <Svg svg={ChangelogLoadingBig} />
        </Flex>
      </AccordionWrapper>
    </Flex>
  )
}

function ChangelogErrorState() {
  return (
    <Flex sx={{ maxHeight: '5.4rem' }} data-testid="changelog-error">
      <AccordionWrapper>
        <ChangelogErrorStateComponent />
      </AccordionWrapper>
    </Flex>
  )
}
